import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { dataProvider } from '@/lib/dataProvider';

interface ClassesListPageProps {
  defaultFilter?: 'all' | 'upcoming' | 'active' | 'completed';
  title?: string;
}

const ClassesFilters: React.FC<{
  filter: string;
  onFilterChange: (filter: 'all' | 'upcoming' | 'active' | 'completed') => void;
  getStatusCount: (status: string) => number;
  organizationFilter: string;
  onOrganizationFilterChange: (orgId: string) => void;
  getOrganizationCount: (orgId: string) => number;
  organizations: any[];
}> = ({ 
  filter, 
  onFilterChange, 
  getStatusCount, 
  organizationFilter, 
  onOrganizationFilterChange, 
  getOrganizationCount,
  organizations
}) => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Status Filter */}
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Lọc theo trạng thái</h3>
          <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => onFilterChange('all')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Tất cả ({getStatusCount('all')})
            </button>
            <button
              onClick={() => onFilterChange('active')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                filter === 'active'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Đang diễn ra ({getStatusCount('active')})
            </button>
            <button
              onClick={() => onFilterChange('upcoming')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                filter === 'upcoming'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sắp diễn ra ({getStatusCount('upcoming')})
            </button>
            <button
              onClick={() => onFilterChange('completed')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                filter === 'completed'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Đã hoàn thành ({getStatusCount('completed')})
            </button>
          </div>
        </div>

        {/* Organization Filter */}
        <div className="lg:w-64">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Lọc theo tổ chức</h3>
          <select
            value={organizationFilter}
            onChange={(e) => onOrganizationFilterChange(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="all">Tất cả tổ chức ({getOrganizationCount('all')})</option>
            {organizations.map((org: any) => (
              <option key={org.id} value={org.id}>
                {org.name} ({getOrganizationCount(org.id)})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Active Filters Display */}
      {organizationFilter !== 'all' && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Bộ lọc đang áp dụng:</h4>
          <div className="flex flex-wrap gap-2">
            {(() => {
              const selectedOrg = organizations.find((org: any) => org.id === organizationFilter);
              return selectedOrg ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <span className={`w-2 h-2 rounded-full mr-2 ${
                    selectedOrg.type === 'internal' ? 'bg-green-500' :
                    selectedOrg.type === 'external' ? 'bg-blue-500' : 'bg-purple-500'
                  }`}></span>
                  Tổ chức: {selectedOrg.name}
                  <button
                    onClick={() => onOrganizationFilterChange('all')}
                    className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-600 hover:bg-blue-200"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ) : null;
            })()}
            <button
              onClick={() => onOrganizationFilterChange('all')}
              className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800"
            >
              Xóa bộ lọc
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ClassesListPage: React.FC<ClassesListPageProps> = ({ 
  defaultFilter = 'all', 
  title = 'Quản lý Lớp học Đào tạo' 
}) => {
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'active' | 'completed'>(defaultFilter);
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const { t } = useLanguage();

  // Get classes from data provider
  const mockClasses: any[] = dataProvider.getClasses();

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 inline-flex text-xs leading-5 font-semibold rounded-full";
    switch (status) {
      case 'upcoming':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'active':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'completed':
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
      case 'cancelled':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return baseClasses;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'Sắp diễn ra';
      case 'active':
        return 'Đang diễn ra';
      case 'completed':
        return 'Đã hoàn thành';
      case 'cancelled':
        return 'Đã hủy';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusCount = (status: string) => {
    if (status === 'all') return mockClasses.length;
    return mockClasses.filter((cls: any) => cls.status === status).length;
  };

  const getOrganizationCount = (orgId: string) => {
    if (orgId === 'all') return mockClasses.length;
    return mockClasses.filter((cls: any) => cls.organization.id === orgId).length;
  };

  const getOrganizationTypeCount = (type: string) => {
    return mockClasses.filter((cls: any) => cls.organization.type === type).length;
  };

  const getOrganizationTypeBadge = (type: string) => {
    const baseClasses = "px-2 py-1 text-xs font-semibold rounded-full";
    switch (type) {
      case 'internal':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'external':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'partner':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      default:
        return baseClasses;
    }
  };

  const getOrganizationTypeText = (type: string) => {
    switch (type) {
      case 'internal': return 'Nội bộ';
      case 'external': return 'Bên ngoài';
      case 'partner': return 'Đối tác';
      default: return type;
    }
  };

  // Apply both filters
  const finalFilteredClasses = mockClasses.filter((cls: any) => {
    const statusMatch = filter === 'all' || cls.status === filter;
    const orgMatch = organizationFilter === 'all' || cls.organization.id === organizationFilter;
    return statusMatch && orgMatch;
  });

  // Get unique organizations for the filter
  const uniqueOrganizations = Array.from(
    new Map(mockClasses.map((cls: any) => [cls.organization.id, cls.organization])).values()
  ).map((org: any) => ({ id: org.id, name: org.name, type: org.type }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-gray-600 mt-1">Quản lý và theo dõi các lớp học đào tạo trong tổ chức</p>
        </div>
        <Link
          to="/lms/classes/new"
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <svg className="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Tạo lớp học mới
        </Link>
      </div>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tổng lớp học</p>
              <p className="text-2xl font-semibold text-gray-900">{mockClasses.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Đang diễn ra</p>
              <p className="text-2xl font-semibold text-gray-900">{getStatusCount('active')}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-orange-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tổng học sinh</p>
              <p className="text-2xl font-semibold text-gray-900">{mockClasses.reduce((sum: any, cls: any) => sum + cls.enrollmentCount, 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tổ chức</p>
              <p className="text-2xl font-semibold text-gray-900">{uniqueOrganizations.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-indigo-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Nội bộ/Bên ngoài</p>
              <p className="text-2xl font-semibold text-gray-900">
                {getOrganizationTypeCount('internal')}/{getOrganizationTypeCount('external')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <ClassesFilters 
        filter={filter} 
        onFilterChange={setFilter} 
        getStatusCount={getStatusCount} 
        organizationFilter={organizationFilter}
        onOrganizationFilterChange={setOrganizationFilter}
        getOrganizationCount={getOrganizationCount}
        organizations={uniqueOrganizations}
      />
      
      {/* Classes Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200" aria-label="Bảng danh sách lớp học">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Tên lớp học & Khóa học
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Giảng viên
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Tổ chức
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Lịch học
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Địa điểm
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Trạng thái
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Số học sinh
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Hành động</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {finalFilteredClasses.map((classItem: any) => (
                <tr key={classItem.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-md bg-indigo-100 text-indigo-700">
                        <svg
                          className="h-6 w-6"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          <Link 
                            to={`/lms/classes/${classItem.id}`} 
                            className="hover:underline text-indigo-600"
                            aria-label={`Xem lớp học ${classItem.title}`}
                          >
                            {classItem.title}
                          </Link>
                        </div>
                        <div className="text-sm text-gray-500">{classItem.courseTitle}</div>
                        <div className="text-xs text-gray-400">#{classItem.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm text-gray-900">{classItem.instructor}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{classItem.organization.name}</div>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={getOrganizationTypeBadge(classItem.organization.type)}>
                        {getOrganizationTypeText(classItem.organization.type)}
                      </span>
                      {classItem.organization.department && (
                        <span className="text-xs text-gray-500">
                          {classItem.organization.department}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(classItem.startDate)} - {formatDate(classItem.endDate)}
                    </div>
                    <div className="text-sm text-gray-500">{classItem.schedule}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{classItem.location}</div>
                    <div className="text-sm text-gray-500">{classItem.room}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(classItem.status)}>
                      {getStatusText(classItem.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-medium">{classItem.enrollmentCount}/{classItem.enrollmentLimit}</span>
                    </div>
                    <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full" 
                        style={{ width: `${(classItem.enrollmentCount / classItem.enrollmentLimit) * 100}%` }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Link
                      to={`/lms/classes/${classItem.id}`}
                      className="text-indigo-600 hover:text-indigo-900"
                      aria-label={`Xem lớp học ${classItem.title}`}
                    >
                      Xem chi tiết
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {finalFilteredClasses.length === 0 && (
        <div className="text-center py-12">
          <svg className="h-16 w-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Không tìm thấy lớp học</h3>
          <p className="text-gray-500">Thử thay đổi bộ lọc để xem thêm lớp học.</p>
        </div>
      )}

      {/* Pagination */}
      {finalFilteredClasses.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between sm:px-6 border border-gray-100 rounded-lg shadow-sm">
          <div className="flex-1 flex justify-between sm:hidden">
            <a
              href="#"
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              aria-label="Trang trước"
            >
              Trước
            </a>
            <a
              href="#"
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              aria-label="Trang sau"
            >
              Tiếp
            </a>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Hiển thị <span className="font-medium">1</span> đến{" "}
                <span className="font-medium">{finalFilteredClasses.length}</span> trong tổng số{" "}
                <span className="font-medium">{finalFilteredClasses.length}</span> kết quả
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Phân trang"
              >
                <a
                  href="#"
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  aria-label="Trang trước"
                >
                  <span className="sr-only">Trước</span>
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
                <a
                  href="#"
                  aria-current="page"
                  className="z-10 bg-indigo-50 border-indigo-500 text-indigo-600 relative inline-flex items-center px-4 py-2 border text-sm font-medium"
                  aria-label="Trang hiện tại, trang 1"
                >
                  1
                </a>
                <a
                  href="#"
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  aria-label="Trang sau"
                >
                  <span className="sr-only">Tiếp</span>
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassesListPage; 